import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/lib/supabase';
import { loadPixel, trackEvent, setPixelStoreId } from '@/lib/facebookPixel';

/**
 * Charge le Facebook Pixel de la boutique et déclenche un événement
 * `PageView` à chaque navigation.
 *
 * Ce composant est monté au niveau du site public de la boutique
 * (TenantStore) : il couvre donc TOUTES les pages publiques du marchand
 * — accueil, catalogue, fiche produit, panier, checkout, réservations,
 * formulaires, pages du thème et pages personnalisées.
 *
 * Il ne s'active que si le marchand a configuré ET activé son pixel
 * depuis /dashboard/facebook-pixel.
 */
export default function FacebookPixel() {
  const { tenant } = useTenant();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const tenantId = tenant?.id ?? null;

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setReady(false);

    // Rattache les futurs événements captés à cette boutique.
    setPixelStoreId(tenantId);

    supabase
      .from('facebook_pixel_config')
      .select('pixel_id, enabled')
      .eq('idcommerce', tenantId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data || !data.enabled || !data.pixel_id) return;
        const ok = loadPixel(data.pixel_id);
        if (ok && !cancelled) setReady(true);
      })
      .catch(() => {
        /* pixel non configuré ou erreur de lecture : on ignore */
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // PageView : à chaque changement de page (le chemin ET les paramètres
  // comptent, une recherche / une fiche produit sont des pages distinctes).
  useEffect(() => {
    if (!ready) return;
    const path = `${location.pathname}${location.search}`;
    trackEvent('PageView', {
      page_path: path,
      page_title: typeof document !== 'undefined' ? document.title : '',
    });
  }, [ready, location.pathname, location.search]);

  return null;
}